import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAuthData, extractAuthFromUrl, clearAuthData } from '../utils/authCookieHelpers';
import { APP_CONFIG } from '../configs/config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      try {
        const authData = getAuthData();
        
        setToken(authData.token);
        setUser(authData.user);
        setIsAuthenticated(authData.isAuthenticated);
      } catch (error) {
        console.error('[AuthContext] Error checking auth:', error);
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    const hasUrlAuth = extractAuthFromUrl();
    if (hasUrlAuth) {
      console.log('[AuthContext] Auth extracted from URL, forcing re-check');
    }

    checkAuth();

    const interval = setInterval(checkAuth, 2000);
    
    return () => clearInterval(interval);
  }, []);

  const logout = () => {
    clearAuthData();
    
    setUser(null);
    setIsAuthenticated(false);
    setToken(null);
    
    window.location.href = `${APP_CONFIG.MAIN_WEBSITE_ORIGIN}/signout`;
  };

  const redirectToSignIn = () => {
    const returnUrl = encodeURIComponent(window.location.href);
    const signInUrl = `${APP_CONFIG.MAIN_WEBSITE_ORIGIN}/signin?returnUrl=${returnUrl}`;
    
    window.location.href = signInUrl;
  };

  const value = { 
    user, 
    isAuthenticated, 
    isLoading, 
    token,
    logout,
    redirectToSignIn
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

